import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useChat } from '@ai-sdk/react';
import {
  AuthenticatedChatTransport,
  authenticatedFetch,
} from './AuthenticatedChatTransport';

export const NEW_CONVERSATION_OPTION = '__new_conversation__';

export const useChatLogic = (
  customerId,
  viewType = 'client',
  canUseAdminControls = false
) => {
  const [hasSubmittedOnce, setHasSubmittedOnce] = useState(false);
  const [debugMode, setDebugMode] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    if (!canUseAdminControls) {
      return false;
    }
    return window.localStorage.getItem('chat_debug_mode') === 'true';
  });
  const [conversationId, setConversationId] = useState(null);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [isConversationsLoading, setIsConversationsLoading] = useState(false);
  const [isConversationMessagesLoading, setIsConversationMessagesLoading] =
    useState(false);
  const [historyError, setHistoryError] = useState(null);

  const conversationIdUpdateSourceRef = useRef('manual');
  const skipHistoryFetchRef = useRef(false);
  const lastStreamConversationIdRef = useRef(null);

  const chatId = useMemo(() => `${viewType}-${customerId}`, [customerId, viewType]);

  const {
    messages: sdkMessages,
    setMessages,
    input: inputValue,
    handleInputChange,
    sendMessage,
    status,
    isLoading,
    error,
    stop,
  } = useChat({
    id: chatId,
    transport: new AuthenticatedChatTransport({ api: '/api/v2/chat/' }),
  });

  const setMessagesRef = useRef(setMessages);
  useEffect(() => {
    setMessagesRef.current = setMessages;
  }, [setMessages]);

  const [localInput, setLocalInput] = useState('');
  const effectiveInput = inputValue ?? localInput;

  useEffect(() => {
    skipHistoryFetchRef.current = false;
    conversationIdUpdateSourceRef.current = 'manual';
    setConversations([]);
    setSelectedConversationId(null);
    setHistoryError(null);
    setHasSubmittedOnce(false);
    setConversationId(null);
    setMessagesRef.current([]);
    setLocalInput('');
    if (!canUseAdminControls) {
      setDebugMode(false);
    } else if (typeof window !== 'undefined') {
      setDebugMode(window.localStorage.getItem('chat_debug_mode') === 'true');
    }
    // `setMessages` comes from the AI SDK hook and may not be referentially stable.
    // Including it in the dependency array causes a reset loop, so we intentionally
    // depend only on customer/view identity changes here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, viewType, canUseAdminControls]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (!canUseAdminControls) {
      window.localStorage.removeItem('chat_debug_mode');
      return;
    }
    window.localStorage.setItem('chat_debug_mode', debugMode ? 'true' : 'false');
  }, [debugMode, canUseAdminControls]);

  const fetchConversations = useCallback(async () => {
    if (!customerId) {
      setConversations([]);
      return;
    }

    setIsConversationsLoading(true);
    try {
      const response = await authenticatedFetch(
        `/api/v2/chat/conversations/?customer_id=${customerId}`
      );

      if (!response.ok) {
        throw new Error(`Failed to load conversations (${response.status})`);
      }

      const payload = await response.json();
      const items = Array.isArray(payload?.conversations)
        ? payload.conversations
        : [];

      setConversations(items);
    } catch (fetchError) {
      console.error('Failed to load conversations', fetchError);
      setConversations([]);
    } finally {
      setIsConversationsLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (!selectedConversationId) {
      setConversationId(null);
      setHistoryError(null);
      setIsConversationMessagesLoading(false);
      setMessagesRef.current([]);
      setHasSubmittedOnce(false);
      return;
    }

    setConversationId(selectedConversationId);

    if (skipHistoryFetchRef.current) {
      skipHistoryFetchRef.current = false;
      return;
    }

    let isCancelled = false;
    setIsConversationMessagesLoading(true);
    setHistoryError(null);

    (async () => {
      try {
        const response = await authenticatedFetch(
          `/api/v2/chat/conversations/${selectedConversationId}/messages/?customer_id=${customerId}`
        );

        if (!response.ok) {
          throw new Error(
            `Failed to load conversation messages (${response.status})`
          );
        }

        const payload = await response.json();
        const rawMessages = Array.isArray(payload?.messages)
          ? payload.messages
          : [];

        if (!isCancelled) {
          setMessagesRef.current(
            rawMessages.map((msg) => ({
              id: msg.id,
              role: msg.role,
              content: msg.content,
              createdAt: msg.created_at,
              metadata: { conversationId: selectedConversationId },
            }))
          );
          setHasSubmittedOnce(rawMessages.length > 0);
        }
      } catch (loadError) {
        if (!isCancelled) {
          console.error('Failed to load conversation messages', loadError);
          setHistoryError(loadError);
          setMessagesRef.current([]);
          setHasSubmittedOnce(false);
        }
      } finally {
        if (!isCancelled) {
          setIsConversationMessagesLoading(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [selectedConversationId, customerId]);

  useEffect(() => {
    for (let i = sdkMessages.length - 1; i >= 0; i -= 1) {
      const message = sdkMessages[i];
      const parts = Array.isArray(message?.parts) ? message.parts : [];

      for (let p = parts.length - 1; p >= 0; p -= 1) {
        const part = parts[p];
        if (part?.type === 'data-message-metadata') {
          const partId = part?.data?.conversationId;
          if (partId && partId !== conversationId) {
            conversationIdUpdateSourceRef.current = 'stream';
            skipHistoryFetchRef.current = true;
            setConversationId(partId);
            return;
          }
        }
      }

      const legacyId = message?.metadata?.conversationId;
      if (legacyId && legacyId !== conversationId) {
        conversationIdUpdateSourceRef.current = 'stream';
        skipHistoryFetchRef.current = true;
        setConversationId(legacyId);
        return;
      }
    }
  }, [sdkMessages, conversationId]);

  useEffect(() => {
    if (!conversationId) {
      lastStreamConversationIdRef.current = null;
      return;
    }

    if (conversationIdUpdateSourceRef.current !== 'stream') {
      return;
    }

    conversationIdUpdateSourceRef.current = 'manual';

    if (lastStreamConversationIdRef.current === conversationId) {
      return;
    }

    lastStreamConversationIdRef.current = conversationId;

    setSelectedConversationId((prev) => prev ?? conversationId);
    fetchConversations();
  }, [conversationId, fetchConversations]);

  const selectConversation = useCallback(
    (conversationIdToSelect) => {
      conversationIdUpdateSourceRef.current = 'manual';
      skipHistoryFetchRef.current = false;
      setHistoryError(null);
      setHasSubmittedOnce(false);
      setMessagesRef.current([]);
      setSelectedConversationId(conversationIdToSelect);
    },
    []
  );

  const startNewConversation = useCallback(() => {
    conversationIdUpdateSourceRef.current = 'manual';
    skipHistoryFetchRef.current = false;
    setHistoryError(null);
    setSelectedConversationId(null);
    setMessagesRef.current([]);
    setHasSubmittedOnce(false);
    setLocalInput('');
  }, []);

  const setPromptValue = useCallback(
    (nextValue) => {
      if (typeof handleInputChange === 'function') {
        handleInputChange({ target: { value: nextValue } });
      } else {
        setLocalInput(nextValue);
      }
    },
    [handleInputChange]
  );

  const onPromptChange = (event) => {
    if (typeof handleInputChange === 'function') {
      handleInputChange(event);
    } else {
      setLocalInput(event.target.value);
    }
  };

  useEffect(() => {
    console.debug('useChat status changed:', status);
  }, [status]);

  useEffect(() => {
    console.debug(
      'useChat messages length:',
      sdkMessages.length,
      'last message:',
      sdkMessages.at(-1)
    );
    const lastMsg = sdkMessages.at(-1);
    if (lastMsg && lastMsg.role === 'assistant') {
      console.debug('Last assistant message parts:', lastMsg.parts);
      console.debug('Last assistant message content:', lastMsg.content);
      console.debug('Status:', status);
    }
  }, [sdkMessages, status]);

  const handleSubmit = async (event) => {
    event?.preventDefault();
    if (!((effectiveInput || '').trim()) || isLoading) {
      return;
    }

    setHasSubmittedOnce(true);

    // Clear input immediately
    if (inputValue === undefined) {
      setLocalInput('');
    } else {
      handleInputChange({ target: { value: '' } });
    }

    await sendMessage(
      { text: effectiveInput },
      {
        body: {
          customer_id: customerId,
          view_type: viewType,
          conversation_id: conversationId ?? selectedConversationId ?? undefined,
          debug_mode: canUseAdminControls && debugMode,
        },
      }
    );

    fetchConversations();
  };

  const sendMessageDirect = useCallback(
    async (text) => {
      if (!((text || '').trim()) || isLoading) {
        return;
      }

      setHasSubmittedOnce(true);

      await sendMessage(
        { text },
        {
          body: {
            customer_id: customerId,
            view_type: viewType,
            conversation_id: conversationId ?? selectedConversationId ?? undefined,
            debug_mode: canUseAdminControls && debugMode,
          },
        }
      );

      fetchConversations();
    },
    [
      isLoading,
      sendMessage,
      customerId,
      viewType,
      conversationId,
      selectedConversationId,
      canUseAdminControls,
      debugMode,
      fetchConversations,
    ]
  );

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const isStreaming = isLoading;
  const showWelcome =
    !selectedConversationId && sdkMessages.length === 0 && !hasSubmittedOnce;

  return {
    messages: sdkMessages,
    effectiveInput,
    onPromptChange,
    handleSubmit,
    handleKeyDown,
    isLoading,
    isStreaming,
    error,
    showWelcome,
    hasSubmittedOnce,
    status,
    conversationId,
    conversations,
    selectedConversationId,
    selectConversation,
    startNewConversation,
    isConversationsLoading,
    isConversationMessagesLoading,
    historyError,
    refreshConversations: fetchConversations,
    stop,
    setPromptValue,
    sendMessageDirect,
    debugMode,
    setDebugMode,
  };
};
